<script>
	import { createEventDispatcher, onMount } from 'svelte';
	import IsoMarker from './IsoMarker.svelte';

	export let items = [];
	export let cell = 60; // cell size in px, lifted so it survives root <-> module view switches
	export let panX = 0;
	export let panY = 0;
	export let editable = false; // creative mode: forwarded to every marker regardless of its own `interactive` flag
	export let selectedKey = null; // key of the marker elevated above overlapping siblings
	export let dimParent = undefined; // parent code whose children get dimmed (undefined = no dimming)
	export let focusKey = null; // selected content-detail, exempt from dimming

	const dispatch = createEventDispatcher();

	const BASE_CELL = 60; // 100% zoom reference
	$: zoomPercent = Math.round((cell / BASE_CELL) * 100);

	// Panning state and handlers
	let dragging = false;
	let pressed = false;
	let dragStart = { x: 0, y: 0 };
	let panStart = { x: 0, y: 0 };
	let raf = null;
	const DRAG_THRESHOLD = 3; // px before we consider it a real pan
	let zoomRaf = null;
	const CELL_MIN = 24;
	const CELL_MAX = 300; // expanded so zooming into detail doesn't hit the ceiling so fast

	// The slider maps to `cell` on a log scale, so an equal slider step is an equal
	// *relative* zoom change at any level — matching the wheel handler's multiplicative
	// feel below. A linear mapping felt fast near CELL_MIN and tedious near CELL_MAX,
	// since a fixed px step is a big % change of a small cell but a tiny % of a large one.
	const LOG_MIN = Math.log(CELL_MIN);
	const LOG_MAX = Math.log(CELL_MAX);
	function cellToSlider(c) {
		return ((Math.log(c) - LOG_MIN) / (LOG_MAX - LOG_MIN)) * 100;
	}
	function sliderToCell(s) {
		return Math.exp(LOG_MIN + (s / 100) * (LOG_MAX - LOG_MIN));
	}
	$: sliderPos = cellToSlider(cell);

	function handleOpen(e) {
		const it = items.find(i => i.key === e?.detail?.id);
		if (!it) return;
		if (it.type === 'module') dispatch('openModule', { code: it.key });
		else dispatch('open', { id: it.key });
	}

	function handleReposition(e) {
		dispatch('reposition', e.detail);
	}
	function handleResize(e) {
		dispatch('resize', e.detail);
	}

	function handlePointerDown(e) {
		// Only start drag on background (not on markers) and with left button
		if (e.button !== 0) return;
		if (e.target && e.target.closest && e.target.closest('.grid-object')) return;
		pressed = true;
		dragStart = { x: e.clientX, y: e.clientY };
		panStart = { x: panX, y: panY };
		e.currentTarget?.setPointerCapture?.(e.pointerId);
		e.preventDefault();
	}

	function handlePointerMove(e) {
		if (!pressed) return;
		const dx = e.clientX - dragStart.x;
		const dy = e.clientY - dragStart.y;
		if (!dragging) {
			if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
			dragging = true;
		}
		const nextX = panStart.x + dx;
		const nextY = panStart.y + dy;
		if (raf) return;
		raf = requestAnimationFrame(() => {
			panX = nextX;
			panY = nextY;
			raf = null;
		});
	}

	function handlePointerUp(e) {
		if (!pressed && !dragging) return;
		pressed = false;
		dragging = false;
		try {
			e.currentTarget?.releasePointerCapture?.(e.pointerId);
		} catch {}
	}
	function handleLostCapture() {
		pressed = false;
		dragging = false;
	}

	function clampCell(value) {
		return Math.min(CELL_MAX, Math.max(CELL_MIN, value));
	}

	// Deltas accumulate here between animation frames so no wheel/trackpad input is
	// lost while a frame is pending — previously, any event that arrived before the
	// scheduled rAF fired was dropped outright instead of being folded in, which is
	// what made rapid-fire wheel/trackpad events feel stuttery.
	let pendingDy = 0;
	let lastMouse = { x: 0, y: 0 };

	function handleWheel(e) {
		// Support mouse wheel and touchpad gesture zoom
		e.preventDefault();
		lastMouse = { x: e.clientX, y: e.clientY };
		const dy = e.deltaMode === 1 ? e.deltaY * 15 : (e.deltaMode === 2 ? e.deltaY * 100 : e.deltaY);
		pendingDy += dy;
		if (zoomRaf) return;
		zoomRaf = requestAnimationFrame(() => {
			const intensity = 0.0012; // tuned for smoothness
			const factor = Math.exp(-pendingDy * intensity);
			pendingDy = 0;
			const oldCell = cell;
			const nextCell = clampCell(oldCell * factor);
			if (nextCell !== oldCell) {
				const { x: mouseX, y: mouseY } = lastMouse;
				// Anchor zoom at cursor: P' = M - ((M - P) / z) * z'
				panX = mouseX - ((mouseX - panX) / oldCell) * nextCell;
				panY = mouseY - ((mouseY - panY) / oldCell) * nextCell;
				cell = nextCell;
				document.documentElement.style.setProperty('--cell', `${cell}px`);
			}
			zoomRaf = null;
		});
	}

	function handleSliderInput(event) {
		cell = clampCell(sliderToCell(Number(event.currentTarget.value)));
		document.documentElement.style.setProperty('--cell', `${cell}px`);
	}

	onMount(() => {
		document.documentElement.style.setProperty('--cell', `${cell}px`);
	});
</script>

<div class="grid-root">
	<div class="toolbar">
		<label>
			Zoom: {zoomPercent}%
			<input type="range" min="0" max="100" step="0.5" value={sliderPos} on:input={handleSliderInput} />
		</label>
		<slot name="toolbar-extra" />
	</div>

	<div class="viewport" class:dragging={dragging} on:pointerdown={handlePointerDown} on:pointermove={handlePointerMove} on:pointerup={handlePointerUp} on:pointercancel={handlePointerUp} on:lostpointercapture={handleLostCapture} on:wheel|nonpassive={handleWheel}>
		<svg class="iso-grid-svg" aria-label="isometric grid" width="100%" height="100%" preserveAspectRatio="none">
			<defs>
				<!-- Diagonal 60° -->
				<pattern id="p-60" patternUnits="userSpaceOnUse" width="{cell}" height="{cell}" patternTransform="translate({panX} {panY}) rotate(60)">
					<line x1="0.5" y1="0" x2="0.5" y2="{cell}" class="grid-line" />
				</pattern>
				<!-- Diagonal 120° -->
				<pattern id="p-120" patternUnits="userSpaceOnUse" width="{cell}" height="{cell}" patternTransform="translate({panX} {panY}) rotate(120)">
					<line x1="0.5" y1="0" x2="0.5" y2="{cell}" class="grid-line" />
				</pattern>
			</defs>
			<!-- Paint the two families of lines as separate layers, grid fills viewport -->
			<rect x="0" y="0" width="100%" height="100%" fill="url(#p-60)" />
			<rect x="0" y="0" width="100%" height="100%" fill="url(#p-120)" />
		</svg>

		<!-- Elements layer -->
		<div class="tiles">
			{#each items as it (it.key)}
				<IsoMarker id={it.key} X={it.X} Y={it.Y} W={it.W} L={it.L} {cell} {panX} {panY} label={it.label} color={it.color} img={it.img} status={it.status} interactive={it.interactive} editable={editable} selected={selectedKey === it.key} dimmed={dimParent !== undefined && it.parent === dimParent && it.key !== focusKey} on:open={handleOpen} on:reposition={handleReposition} on:resize={handleResize}/>
			{/each}
		</div>
	</div>
</div>

<style>
	.grid-root {
		position: absolute;
		inset: 0;
		overflow: hidden;
	}

	/* Viewport */
	.viewport {
		position: absolute;
		inset: 0;
		user-select: none;
		touch-action: none;
		cursor: default;
		/* warm organic depth */
		background:
			radial-gradient(ellipse 70% 55% at 15% 15%, rgba(60, 20, 120, 0.28) 0%, transparent 65%),
			radial-gradient(ellipse 55% 45% at 85% 85%, rgba(0, 100, 120, 0.14) 0%, transparent 65%),
			var(--bg);
	}
	.viewport.dragging { cursor: grabbing; }

	/* Grain overlay on the grid */
	.viewport::after {
		content: '';
		position: absolute;
		inset: 0;
		pointer-events: none;
		z-index: 20;
		opacity: 0.032;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E");
		background-size: 200px 200px;
	}

	.iso-grid-svg {
		position: absolute;
		inset: 0;
		pointer-events: none;
		display: block;
	}
	.grid-line {
		stroke: var(--line);
		stroke-width: 1;
		shape-rendering: crispEdges;
	}
	.tiles {
		position: absolute;
		inset: 0;
		pointer-events: auto;
	}
	.tiles :global(.marker) {
		pointer-events: auto;
		z-index: 5;
	}

	/* Toolbar */
	.toolbar {
		position: absolute;
		top: calc(var(--nav-h, 56px) + 12px);
		left: 12px;
		z-index: 110;
		background: rgba(12, 10, 21, 0.82);
		color: #dde8f0;
		padding: 8px 12px;
		border-radius: 10px;
		border: 1px solid rgba(0, 229, 255, 0.15);
		backdrop-filter: blur(10px);
		box-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 2px 6px rgba(80,40,160,0.2);
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.toolbar input[type="range"] {
		width: 160px;
		accent-color: var(--cyan);
	}

	.toolbar label {
		display: flex;
		align-items: center;
		gap: 6px;
		cursor: pointer;
		font-size: 13px;
	}
</style>
